import { EventType } from "@/analytics/analytics-events";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { useAnnotationPermissions } from "@/hooks/use-annotation-permissions";
import { GetAsBuiltEntry } from "@/modes/ipx-order-mode/ipx-entry-points";
import { selectAllCadModels } from "@/store/cad/cad-selectors";
import {
  selectActiveCadId,
  selectActiveCadLoadingError,
  setActiveCad,
} from "@/store/cad/cad-slice";
import { Features } from "@/store/features/features";
import { selectHasFeature } from "@/store/features/features-slice";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import {
  selectCanGenerateFloorplans,
  selectCanReadCAD,
} from "@/store/subscription-features/subscription-features-selectors";
import { TabType, setActiveTab } from "@/store/ui/ui-slice";
import { selectHasWritePermission } from "@/store/user-selectors";
import { TreeData } from "@/utils/tree-generation/tree-data";
import {
  Dropdown,
  selectChildDepthFirst,
  selectIElement,
  useBreakpointMdUp,
} from "@faro-lotv/app-component-toolbox";
import {
  Alert,
  DeleteIcon,
  ExperimentalIcon,
  FaroIconButton,
  FaroText,
  FaroTooltip,
  FontWeights,
  NoTranslate,
  Option,
  useDialog,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { GUID } from "@faro-lotv/foundation";
import {
  IElementModel3dStream,
  isIElementBimModel,
} from "@faro-lotv/ielement-types";
import { TabContext, TabList } from "@mui/lab";
import {
  Box,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Tab,
  Typography,
} from "@mui/material";
import { isEqual } from "es-toolkit";
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  type JSX,
} from "react";
import { getCadSpatialGridOption } from "../common/point-cloud-file-upload-context/cad-upload-utils";
import { useCurrentProjectApiClient } from "../common/project-provider/project-loading-context";
import { AnnotationPanel } from "./annotation-panel/annotation-panel";
import { CadModelTree } from "./tree/cad-model-tree/cad-model-tree";
import { deleteCad } from "./tree/cad-model-tree/cad-model-tree-context-menu";
import { useCadModelTreeData } from "./tree/cad-model-tree/cad-model-tree-data";
import { CaptureTree } from "./tree/capture-tree/capture-tree";
import { CaptureTreeNode } from "./tree/capture-tree/capture-tree-node";
import { GenerateFloorPlanFeatureAlert } from "./tree/capture-tree/generate-floorplan-feature-alert";
import { ProjectFilter } from "./tree/capture-tree/project-filter";
import { useTreeContext } from "./tree/tree-context";
import { TreeHeightWrapper } from "./tree/tree-height-wrapper";

/**
 * UI component including the data sessions tree either alone, or with the CAD model tree in a separate tab.
 *
 * @returns JSX.Element UI component displaying the tab "Capture" and the tab "Model" and their contents.
 */
export function ProjectView(): JSX.Element {
  const { canReadAnnotations: showAnnotationTab } = useAnnotationPermissions();

  const { tree } = useTreeContext();

  return (
    <TabbedProjectView tree={tree} showAnnotationTab={showAnnotationTab} />
  );
}

type TabbedProjectViewProps = {
  /** True to show the annotation tab */
  showAnnotationTab: boolean;

  /** The project content tree */
  tree: TreeData[];
};

/**
 * Display the tabs with both the "Capture" (list of data sessions) and "Model" (list of CADs) tree views.
 *
 * @returns UI component including 2 tabs and their contents
 */
function TabbedProjectView({
  showAnnotationTab,
  tree,
}: TabbedProjectViewProps): JSX.Element {
  const dispatch = useAppDispatch();
  const activeTab = useAppSelector((state) => state.ui.activeTab);

  const handleTabChange = useCallback(
    (_: unknown, newValue: TabType): void => {
      dispatch(setActiveTab(newValue));
    },
    [dispatch],
  );

  const canGenerateFloorPlans = useAppSelector(selectCanGenerateFloorplans);

  return (
    <Stack component="div" sx={{ flexGrow: "1", overflow: "auto" }}>
      <TabContext value={activeTab.toString()}>
        <Box component="div">
          <TabList onChange={handleTabChange}>
            <Tab label="Capture" value={TabType.capture} />
            <Tab label="3D Model" value={TabType.model} />
            {showAnnotationTab && (
              <Tab
                label="Annotations"
                value={TabType.annotations}
                onClick={() => Analytics.track(EventType.openAnnotationList)}
              />
            )}
          </TabList>
        </Box>
      </TabContext>

      <TabPanel visible={activeTab === TabType.capture}>
        <ProjectFilter />
        {canGenerateFloorPlans && <GenerateFloorPlanFeatureAlert />}
        <TreeHeightWrapper>
          <CaptureTree tree={tree}>{CaptureTreeNode}</CaptureTree>
        </TreeHeightWrapper>
      </TabPanel>

      <TabPanel visible={activeTab === TabType.model}>
        <CadPanel />
      </TabPanel>

      {showAnnotationTab && (
        <TabPanel visible={activeTab === TabType.annotations}>
          <AnnotationPanel />
        </TabPanel>
      )}
    </Stack>
  );
}

type TabPanelProps = PropsWithChildren<{
  visible: boolean;
}>;

function TabPanel({ children, visible }: TabPanelProps): JSX.Element {
  const isMdUp = useBreakpointMdUp();

  return (
    <Stack
      sx={{
        overflow: "auto",
        flexGrow: 1,
        flexDirection: "column",
        ...(isMdUp ? { mt: 2, ml: 2.5 } : { mt: 1 }),
        display: visible ? undefined : "none",
      }}
    >
      {children}
    </Stack>
  );
}

type StreamCadModelLabelProps = {
  cad: IElementModel3dStream;
};
/**
 * @returns Label component with extra information for CAD model optimized for out-of-core streaming
 */
function StreamCadModelLabel({ cad }: StreamCadModelLabelProps): JSX.Element {
  const parent = useAppSelector(selectIElement(cad.parentId));
  const cadFile = useAppSelector(
    selectChildDepthFirst(parent, isIElementBimModel),
  );
  const cadSpatialGridOption = useMemo(() => {
    const fileName = cadFile?.fileName;
    if (!fileName) return;

    return getCadSpatialGridOption(fileName);
  }, [cadFile?.fileName]);

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      {cadSpatialGridOption && (
        <FaroTooltip
          title={
            <Stack>
              <FaroText variant="labelM" dark>
                Optimized for Out-of-core Rendering
              </FaroText>
              <FaroText variant="bodyS" dark>
                {`StaticChunks: ${cadSpatialGridOption.numberOfStaticChunks}`}
              </FaroText>
              <FaroText variant="bodyS" dark>
                {`StaticChunkSize: ${cadSpatialGridOption.staticChunkByteSize / 1024 / 1024} MB`}
              </FaroText>
              <FaroText variant="bodyS" dark>
                {`DynamicChunkFileSize: ${cadSpatialGridOption.dynamicChunkByteFileSize / 1024 / 1024} MB`}
              </FaroText>
              <FaroText variant="bodyS" dark>
                {`GridCellWidth: ${cadSpatialGridOption.gridCellWidth} M`}
              </FaroText>
              <FaroText variant="bodyS" dark>
                {`DynamicChunkMemorySize: ${cadSpatialGridOption.dynamicChunkByteMemorySize ? `${cadSpatialGridOption.dynamicChunkByteMemorySize / 1024 / 1024} MB` : "N/A"}`}
              </FaroText>
              <FaroText variant="bodyS" dark>
                {`Collate small chunks: ${cadSpatialGridOption.doNotCollateSmallChunks ? "No" : "Yes"}`}
              </FaroText>
            </Stack>
          }
        >
          <ExperimentalIcon
            sx={{
              width: "18px",
              height: "18px",
            }}
          />
        </FaroTooltip>
      )}
      <NoTranslate>{cad.name}</NoTranslate>
    </Stack>
  );
}

function CadPanel(): JSX.Element {
  const dispatch = useAppDispatch();

  const streamCadModelOutOfCore = useAppSelector(
    selectHasFeature(Features.StreamCadModelOutOfCore),
  );

  const currentCadId = useAppSelector(selectActiveCadId);
  const cadModels = useAppSelector(selectAllCadModels, isEqual);
  const hasValidCadLicense = useAppSelector(selectCanReadCAD);

  // sort models in alphabetical order of names
  const sortedCadModels = useMemo(
    () => cadModels.sort((a, b) => a.name.localeCompare(b.name)),
    [cadModels],
  );

  const cadSelectorOptions: Option[] = sortedCadModels.map((cad) => ({
    key: cad.id,
    value: cad.id,
    label: streamCadModelOutOfCore ? (
      <StreamCadModelLabel cad={cad} />
    ) : (
      <NoTranslate>{cad.name}</NoTranslate>
    ),
  }));

  // if there is no active CAD yet, take first one in the lost and set it as default active CAD
  useEffect(() => {
    if (!currentCadId && sortedCadModels.length) {
      dispatch(setActiveCad(sortedCadModels[0].id));
    }
  }, [currentCadId, dispatch, sortedCadModels]);

  const modelTreeData = useCadModelTreeData();
  const activeCadLoadingError = useAppSelector(selectActiveCadLoadingError);
  const currentWarning = hasValidCadLicense
    ? activeCadLoadingError
    : "3D Model Support add-on required. Enhance your experience with stunning 3D visualizations.";

  const hasModels = cadModels.length > 0;

  return (
    <>
      <GetAsBuiltEntry position="top" />

      {currentWarning && (
        <Alert title={currentWarning} variant="warning" sx={{ mb: 3 }} />
      )}

      {hasValidCadLicense && hasModels && (
        <>
          <Dropdown
            label="Active model"
            options={cadSelectorOptions}
            sx={{ height: 35 }}
            value={currentCadId ?? ""}
            shouldCapitalize={false}
            onChange={(e) => {
              const selCad = cadSelectorOptions.find(
                (el) => el.value === e.target.value,
              );
              if (selCad) {
                Analytics.track(EventType.selectCadModel);
                dispatch(setActiveCad(selCad.key));
              }
            }}
          />
          {typeof modelTreeData === "string" ? (
            <Typography>{modelTreeData}</Typography>
          ) : (
            <TreeHeightWrapper>
              <CadModelTree
                modelTreeData={modelTreeData}
                hasValidCadLicense={hasValidCadLicense}
              />
            </TreeHeightWrapper>
          )}
        </>
      )}

      {!hasValidCadLicense && hasModels && (
        <SelectAndDeleteCad options={cadSelectorOptions} />
      )}
    </>
  );
}

type SelectAndDeleteCadProps = {
  /** Options for the dropdown */
  options: Option[];
};

/**
 *
 * @returns JSX.Element UI component providing way to select and delete 3D model
 * without need to build model tree and set active CAD
 */
function SelectAndDeleteCad({ options }: SelectAndDeleteCadProps): JSX.Element {
  const { getState } = useAppStore();
  const dispatch = useAppDispatch();
  const projectApi = useCurrentProjectApiClient();
  const { createDialog } = useDialog();
  const { handleErrorWithDialog } = useErrorHandlers();

  // If the user has permission to edit the project or not
  const hasWritePermission = useAppSelector(selectHasWritePermission);

  const deleteCadCallback = useCallback(
    async (elementId: GUID) => {
      try {
        await deleteCad(
          elementId,
          getState(),
          dispatch,
          projectApi,
          createDialog,
        );
      } catch (error) {
        handleErrorWithDialog({
          title: "Error: failed to delete the 3D Model",
          error,
        });
      }
    },
    [createDialog, dispatch, getState, handleErrorWithDialog, projectApi],
  );

  return (
    <>
      <FaroText variant="bodyS" sx={{ fontWeight: FontWeights.Bold, mb: 0.7 }}>
        Active model
      </FaroText>
      <Select value="" sx={{ height: 35, minWidth: 200 }}>
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <ListItemText primary={option.label} />
            {hasWritePermission && (
              <FaroIconButton
                onClick={(e) => {
                  e.stopPropagation();
                  deleteCadCallback(option.key);
                }}
                size="s"
                margin={0.5}
              >
                <DeleteIcon />
              </FaroIconButton>
            )}
          </MenuItem>
        ))}
      </Select>
      <FaroText variant="bodyL" sx={{ mb: 0.7 }}>
        No active 3D model tree
      </FaroText>
    </>
  );
}
